import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { formatCurrency as formatCurrencyLib } from '../lib/currency';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  Wallet,
  Plus,
  Check,
  AlertTriangle,
  ArrowRight,
  DollarSign,
  Calendar,
  HelpCircle,
  Info,
} from 'lucide-react';
import { toast } from 'sonner';

// Helper component for tooltip info icons
const InfoTooltip = ({ children }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <HelpCircle size={14} className="inline-block ml-1 text-[hsl(var(--muted-ink))] cursor-help" />
    </TooltipTrigger>
    <TooltipContent side="top" className="max-w-xs bg-[hsl(var(--ink-1))] text-white">
      <p className="text-sm">{children}</p>
    </TooltipContent>
  </Tooltip>
);

const Reconciliation = () => {
  const { company, canEdit } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [reconciliations, setReconciliations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showStartDialog, setShowStartDialog] = useState(false);
  const [showWorkspace, setShowWorkspace] = useState(false);
  const [activeRecon, setActiveRecon] = useState(null);
  const [selectedTxns, setSelectedTxns] = useState(new Set());
  
  const [startForm, setStartForm] = useState({
    account_id: '',
    period: new Date().toISOString().slice(0, 7), // "2024-03"
    statement_date: new Date().toISOString().split('T')[0],
    statement_opening_balance: '',
    statement_closing_balance: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [acctRes, reconRes] = await Promise.all([
        api.get('/reconciliation/accounts'),
        api.get('/reconciliation')
      ]);
      setAccounts(acctRes.data);
      setReconciliations(reconRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load reconciliation data');
    } finally {
      setLoading(false);
    }
  };

  const handleStartReconciliation = async () => {
    if (!startForm.account_id || !startForm.statement_closing_balance) {
      toast.error('Please fill in required fields');
      return;
    }
    
    try {
      const res = await api.post('/reconciliation/start', {
        ...startForm,
        statement_opening_balance: parseFloat(startForm.statement_opening_balance) || 0,
        statement_closing_balance: parseFloat(startForm.statement_closing_balance)
      });
      
      toast.success('Reconciliation started! Now match your transactions.');
      setShowStartDialog(false);
      setActiveRecon(res.data);
      setShowWorkspace(true);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start reconciliation');
    }
  };

  const openReconciliation = async (reconId) => {
    try {
      const res = await api.get(`/reconciliation/${reconId}`);
      setActiveRecon(res.data);
      // Pre-select already reconciled transactions
      const reconciledIds = (res.data.transactions || [])
        .filter(t => t.is_reconciled)
        .map(t => t.id);
      setSelectedTxns(new Set(reconciledIds));
      setShowWorkspace(true);
    } catch (error) {
      toast.error('Failed to load reconciliation');
    }
  };

  const toggleTransaction = (txnId) => {
    const newSelected = new Set(selectedTxns);
    if (newSelected.has(txnId)) {
      newSelected.delete(txnId);
    } else {
      newSelected.add(txnId);
    }
    setSelectedTxns(newSelected);
  };

  const saveProgress = async () => {
    try {
      await api.put(`/reconciliation/${activeRecon.id}`, {
        reconciled_transaction_ids: Array.from(selectedTxns)
      });
      toast.success('Progress saved successfully!');
      openReconciliation(activeRecon.id);
    } catch (error) {
      toast.error('Failed to save progress');
    }
  };

  const completeReconciliation = async () => {
    try {
      await api.post(`/reconciliation/${activeRecon.id}/complete`);
      toast.success('Reconciliation completed successfully! Your records now match your bank statement.');
      setShowWorkspace(false);
      setActiveRecon(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete reconciliation');
    }
  };

  const formatCurrency = (amount) => {
    return formatCurrencyLib(amount || 0, company?.currency || 'USD');
  };

  const getStatusBadge = (status) => {
    if (status === 'completed') {
      return <Badge className="bg-[hsl(var(--success-soft))] text-[hsl(var(--success))]">Completed</Badge>;
    }
    return <Badge className="bg-[hsl(var(--warning-soft))] text-[hsl(var(--warning))]">In Progress</Badge>;
  };

  // Calculate totals for selected transactions
  const calculateSelectedTotal = () => {
    if (!activeRecon?.transactions) return 0;
    return activeRecon.transactions
      .filter(t => selectedTxns.has(t.id))
      .reduce((sum, t) => {
        const amount = parseFloat(t.amount) || 0;
        return t.transaction_type === 'revenue' ? sum + amount : sum - amount;
      }, 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[hsl(var(--brand-500))]"></div>
      </div>
    );
  }

  // Workspace View
  if (showWorkspace && activeRecon) {
    const difference = parseFloat(activeRecon.statement_closing_balance) - parseFloat(activeRecon.book_balance || 0);
    const isBalanced = Math.abs(difference) < 0.01;
    
    return (
      <TooltipProvider>
        <div className="space-y-6" data-testid="reconciliation-workspace">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-[hsl(var(--ink-1))]" style={{ fontFamily: 'Space Grotesk' }}>
                Reconciling: {activeRecon.account?.name}
              </h1>
              <p className="text-[hsl(var(--muted-ink))]">
                Period: {activeRecon.period} • Statement Date: {activeRecon.statement_date}
              </p>
            </div>
            <Button variant="outline" onClick={() => { setShowWorkspace(false); setActiveRecon(null); }}>
              Back to List
            </Button>
          </div>

          {/* Instruction Banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
            <Info size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">How to reconcile:</p>
              <ol className="list-decimal list-inside space-y-1">
                <li>Compare transactions below with your bank statement</li>
                <li>Check the box next to each transaction that appears on your statement</li>
                <li>The "Difference" should become zero when all matching transactions are selected</li>
                <li>Click "Complete" when the difference is zero</li>
              </ol>
            </div>
          </div>

          {/* Balance Summary */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-[hsl(var(--muted-ink))] flex items-center">
                  Statement Balance
                  <InfoTooltip>The closing balance shown on your bank statement for this period.</InfoTooltip>
                </p>
                <p className="text-2xl font-bold">{formatCurrency(activeRecon.statement_closing_balance)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-[hsl(var(--muted-ink))] flex items-center">
                  Book Balance
                  <InfoTooltip>The balance according to your records in Simple Financials. This should match your statement balance.</InfoTooltip>
                </p>
                <p className="text-2xl font-bold">{formatCurrency(activeRecon.book_balance)}</p>
              </CardContent>
            </Card>
            <Card className={isBalanced ? 'border-[hsl(var(--success))] border-2' : 'border-[hsl(var(--danger))] border-2'}>
              <CardContent className="pt-6">
                <p className="text-sm text-[hsl(var(--muted-ink))] flex items-center">
                  Difference
                  <InfoTooltip>The gap between your statement and book balance. This must be zero to complete reconciliation.</InfoTooltip>
                </p>
                <p className={`text-2xl font-bold ${isBalanced ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--danger))]'}`}>
                  {formatCurrency(difference)}
                </p>
                {isBalanced && (
                  <p className="text-xs text-[hsl(var(--success))] mt-1 flex items-center gap-1">
                    <Check size={12} /> Ready to complete
                  </p>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-[hsl(var(--muted-ink))] flex items-center">
                  Selected Items
                  <InfoTooltip>Number of transactions you've matched with your bank statement.</InfoTooltip>
                </p>
                <p className="text-2xl font-bold">{selectedTxns.size} items</p>
              </CardContent>
            </Card>
          </div>

          {/* Transactions to Reconcile */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div className="flex items-center gap-2">
                <CardTitle>Transactions</CardTitle>
                <InfoTooltip>These are unreconciled transactions for the selected account and period. Match them with your bank statement.</InfoTooltip>
              </div>
              <div className="flex gap-2">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="outline" onClick={saveProgress} data-testid="save-progress-btn">
                      Save Progress
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="bg-[hsl(var(--ink-1))] text-white">
                    Save your work and continue later
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button 
                        onClick={completeReconciliation} 
                        disabled={!isBalanced}
                        className="bg-[hsl(var(--brand-500))] hover:bg-[hsl(var(--brand-600))]"
                        data-testid="complete-recon-btn"
                      >
                        <Check size={16} className="mr-2" />
                        Complete
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="bg-[hsl(var(--ink-1))] text-white">
                    {isBalanced 
                      ? "Finalize this reconciliation" 
                      : "Difference must be zero to complete"}
                  </TooltipContent>
                </Tooltip>
              </div>
            </CardHeader>
            <CardContent>
              {(activeRecon.transactions || []).length === 0 ? (
                <div className="text-center py-8">
                  <Wallet className="mx-auto h-12 w-12 text-[hsl(var(--muted-ink))]" />
                  <p className="mt-4 text-[hsl(var(--muted-ink))]">No unreconciled transactions for this period</p>
                  <p className="text-sm text-[hsl(var(--muted-ink))] mt-2">
                    If your difference is zero, you can complete the reconciliation.
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <span className="flex items-center">
                          Match
                          <InfoTooltip>Check transactions that appear on your bank statement</InfoTooltip>
                        </span>
                      </TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(activeRecon.transactions || []).map((txn) => (
                      <TableRow key={txn.id} className={txn.is_reconciled ? 'bg-green-50 opacity-70' : selectedTxns.has(txn.id) ? 'bg-blue-50' : ''}>
                        <TableCell>
                          <Checkbox
                            checked={selectedTxns.has(txn.id) || txn.is_reconciled}
                            disabled={txn.is_reconciled}
                            onCheckedChange={() => toggleTransaction(txn.id)}
                            data-testid={`recon-checkbox-${txn.id}`}
                          />
                        </TableCell>
                        <TableCell>{txn.date}</TableCell>
                        <TableCell>{txn.description || txn.reference_number}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {txn.transaction_type?.replace('_', ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell className={`text-right font-medium ${txn.transaction_type === 'revenue' ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--danger))]'}`}>
                          {txn.transaction_type === 'revenue' ? '+' : '-'}{formatCurrency(txn.amount)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      </TooltipProvider>
    );
  }

  // Main List View
  return (
    <TooltipProvider>
      <div className="space-y-6" data-testid="reconciliation-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[hsl(var(--ink-1))]" style={{ fontFamily: 'Space Grotesk' }}>
              Bank Reconciliation
            </h1>
            <p className="text-[hsl(var(--muted-ink))]">
              Match your bank statements to your records to ensure accuracy
            </p>
          </div>
          {canEdit && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  onClick={() => setShowStartDialog(true)}
                  className="bg-[hsl(var(--brand-500))] hover:bg-[hsl(var(--brand-600))]"
                  data-testid="start-reconciliation-btn"
                >
                  <Plus size={16} className="mr-2" />
                  Start Reconciliation
                </Button>
              </TooltipTrigger>
              <TooltipContent className="bg-[hsl(var(--ink-1))] text-white">
                Begin reconciling a bank or cash account with your statement
              </TooltipContent>
            </Tooltip>
          )}
        </div>

        {/* What is Reconciliation Banner */}
        <div className="bg-[hsl(var(--brand-50))] border border-[hsl(var(--brand-200))] rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Info size={20} className="text-[hsl(var(--brand-600))] flex-shrink-0 mt-0.5" />
            <div className="text-sm text-[hsl(var(--brand-700))]">
              <p className="font-medium mb-1">What is Bank Reconciliation?</p>
              <p>
                Reconciliation ensures your records match your actual bank statements. 
                It helps you find missing transactions, identify errors, and confirm your financial data is accurate.
              </p>
            </div>
          </div>
        </div>

        {/* Accounts Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {accounts.map((account) => (
            <Card key={account.id} className="hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-[hsl(var(--brand-50))]">
                      <Wallet className="h-5 w-5 text-[hsl(var(--brand-600))]" />
                    </div>
                    <div>
                      <p className="font-medium">{account.name}</p>
                      <p className="text-sm text-[hsl(var(--muted-ink))] capitalize">{account.account_subtype}</p>
                    </div>
                  </div>
                </div>
                {account.last_reconciliation ? (
                  <div className="text-sm space-y-1">
                    <p className="text-[hsl(var(--muted-ink))]">
                      Last reconciled: {account.last_reconciliation.period}
                    </p>
                    {getStatusBadge(account.last_reconciliation.status)}
                  </div>
                ) : (
                  <p className="text-sm text-[hsl(var(--muted-ink))]">Never reconciled</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Reconciliation History */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Reconciliation History
              <InfoTooltip>A record of all your past and ongoing reconciliations.</InfoTooltip>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {reconciliations.length === 0 ? (
              <div className="text-center py-12">
                <Wallet className="mx-auto h-12 w-12 text-[hsl(var(--muted-ink))]" />
                <h3 className="mt-4 text-lg font-medium">No reconciliations yet</h3>
                <p className="mt-2 text-[hsl(var(--muted-ink))]">Start reconciling your bank accounts to keep your records accurate</p>
                {canEdit && (
                  <Button 
                    onClick={() => setShowStartDialog(true)}
                    className="mt-4 bg-[hsl(var(--brand-500))] hover:bg-[hsl(var(--brand-600))]"
                  >
                    <Plus size={16} className="mr-2" />
                    Start Your First Reconciliation
                  </Button>
                )}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Account</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead>Statement Date</TableHead>
                    <TableHead>Statement Balance</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reconciliations.map((recon) => (
                    <TableRow key={recon.id}>
                      <TableCell className="font-medium">{recon.account?.name}</TableCell>
                      <TableCell>{recon.period}</TableCell>
                      <TableCell>{recon.statement_date}</TableCell>
                      <TableCell>{formatCurrency(recon.statement_closing_balance)}</TableCell>
                      <TableCell>{getStatusBadge(recon.status)}</TableCell>
                      <TableCell>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => openReconciliation(recon.id)}
                          data-testid={`view-recon-${recon.id}`}
                        >
                          {recon.status === 'in_progress' ? 'Continue' : 'View'}
                          <ArrowRight size={14} className="ml-1" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Start Dialog */}
        <Dialog open={showStartDialog} onOpenChange={setShowStartDialog}>
          <DialogContent data-testid="start-recon-dialog">
            <DialogHeader>
              <DialogTitle>Start Bank Reconciliation</DialogTitle>
              <DialogDescription>
                Enter the details from your bank statement to begin reconciling.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="flex items-center">
                  Bank Account *
                  <InfoTooltip>Select the bank or cash account you want to reconcile.</InfoTooltip>
                </Label>
                <Select 
                  value={startForm.account_id} 
                  onValueChange={(v) => setStartForm({...startForm, account_id: v})}
                >
                  <SelectTrigger data-testid="recon-account-select">
                    <SelectValue placeholder="Select account" />
                  </SelectTrigger>
                  <SelectContent>
                    {accounts.map((a) => (
                      <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center">
                  Statement Period *
                  <InfoTooltip>The month and year of your bank statement (e.g., January 2025).</InfoTooltip>
                </Label>
                <Input
                  type="month"
                  value={startForm.period}
                  onChange={(e) => setStartForm({...startForm, period: e.target.value})}
                  data-testid="recon-period-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center">
                  Statement Date *
                  <InfoTooltip>The closing date shown on your bank statement (usually the last day of the month).</InfoTooltip>
                </Label>
                <Input
                  type="date"
                  value={startForm.statement_date}
                  onChange={(e) => setStartForm({...startForm, statement_date: e.target.value})}
                  data-testid="recon-statement-date"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center">
                  Opening Balance
                  <InfoTooltip>The starting balance on your bank statement for this period. Leave as 0 for your first reconciliation.</InfoTooltip>
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  value={startForm.statement_opening_balance}
                  onChange={(e) => setStartForm({...startForm, statement_opening_balance: e.target.value})}
                  placeholder="0.00"
                  data-testid="recon-opening-balance"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center">
                  Statement Closing Balance *
                  <InfoTooltip>The ending balance shown on your bank statement. This is the key number your records should match.</InfoTooltip>
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  value={startForm.statement_closing_balance}
                  onChange={(e) => setStartForm({...startForm, statement_closing_balance: e.target.value})}
                  placeholder="0.00"
                  data-testid="recon-closing-balance"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowStartDialog(false)}>Cancel</Button>
              <Button 
                onClick={handleStartReconciliation}
                className="bg-[hsl(var(--brand-500))] hover:bg-[hsl(var(--brand-600))]"
                data-testid="confirm-start-recon"
              >
                Start Reconciliation
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
};

export default Reconciliation;
